import { TEMPLATES, type TemplateId } from '@doriavboard/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage } from '../api/client';
import { toast } from '../stores/toasts';
import { TemplatePreview } from './TemplatePreview';
import { Spinner } from './ui/Spinner';

/** The template a board starts from, and the title it gets from it. */
export function templateTitle(template: TemplateId): string | undefined {
  return template === 'blank' ? undefined : TEMPLATES.find((t) => t.id === template)?.name;
}

interface NewBoardStripProps {
  heading: string;
  /** Creates the board and resolves to its id, which is then opened. */
  create(template: TemplateId): Promise<string>;
}

/** "Start a new board": one button per template. */
export function NewBoardStrip({ heading, create }: NewBoardStripProps) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState<TemplateId | null>(null);

  async function start(template: TemplateId) {
    setCreating(template);
    try {
      navigate(`/b/${await create(template)}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
      setCreating(null);
    }
  }

  return (
    <section className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/40">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <h2 className="mb-4 text-sm font-medium text-slate-600 dark:text-slate-300">{heading}</h2>
        <div className="flex gap-4 overflow-x-auto pb-2">
          {TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={creating !== null}
              onClick={() => start(t.id)}
              className="focus-ring group w-40 shrink-0 rounded-xl text-left disabled:opacity-60"
              title={t.description}
            >
              <div className="relative aspect-[16/10] overflow-hidden rounded-xl border border-slate-200 transition group-hover:border-brand-400 group-hover:shadow-float dark:border-slate-700">
                <TemplatePreview id={t.id} />
                {creating === t.id && (
                  <div className="absolute inset-0 grid place-items-center bg-white/70 dark:bg-slate-900/70">
                    <Spinner className="size-6 text-brand-600" />
                  </div>
                )}
              </div>
              <p className="mt-2 text-sm font-medium">{t.name}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
